import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  CheckCircle,
  Loader2,
  Upload,
  CreditCard,
  AlertCircle
} from 'lucide-react';
import { useSelector } from 'react-redux';
import { getCourier, getCourierSetting } from '../../state/courierSlice';
import { getUser, getIsAuthenticated } from '../../state/authSlice';
import { apiConfig } from '../../shared/api-config';
import { showErrorToast, showSuccessToast } from '../../utils/toast';
import { useCart } from '../../context/CartContext';
import { useDarkMode } from '../../context/DarkModeContext';
import { ShopOrderApi, ShopOrderItemApi, BankAccountApi, BankAccount } from '@shared/sdk-v1';

const ShopCart: React.FC = () => {
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const location = useLocation();
  const courier = useSelector(getCourier);
  const courierSetting = useSelector(getCourierSetting);
  const user = useSelector(getUser);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const { cartItems, removeFromCart, updateQuantity, clearCart, getTotalPrice } = useCart();

  // Determine if we're in checkout mode
  const isCheckout = location.pathname.includes('/checkout');

  // Determine default payment method — prefer online gateway over bank transfer
  const defaultPaymentMethod = courierSetting?.isStripeApiEnabled
    ? 'stripe'
    : courierSetting?.isFygaroApiEnabled
    ? 'fygaro'
    : courierSetting?.isWipayApiEnabled
    ? 'wipay'
    : 'bank_transfer';

  // State
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>(defaultPaymentMethod);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderNotes, setOrderNotes] = useState('');
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');

  // Fetch bank accounts when entering checkout
  const fetchBankAccounts = useCallback(async () => {
    if (!courier?.id) return;
    try {
      const api = new BankAccountApi(apiConfig());
      const data = await api.bankAccountFind({
        filter: JSON.stringify({
          where: { courierId: courier.id, isActive: true },
          order: 'sortOrder ASC, createdAt ASC',
        }),
      });
      setBankAccounts(data);
    } catch (e) {
      console.error('Failed to fetch bank accounts:', e);
    }
  }, [courier?.id]);

  useEffect(() => {
    if (isCheckout) fetchBankAccounts();
  }, [isCheckout, fetchBankAccounts]);

  // Get condition badge color
  const getConditionBadge = (condition?: string) => {
    const baseClasses = 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium';
    
    switch (condition?.toLowerCase()) {
      case 'new':
        return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'like_new':
        return `${baseClasses} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
      case 'good':
        return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'fair':
        return `${baseClasses} bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`;
    }
  };

  // Place order
  const handlePlaceOrder = async () => {
    if (cartItems.length === 0) {
      showErrorToast({ title: 'Error', message: 'Cart is empty' });
      return;
    }

    if (!courier?.id) {
      showErrorToast({ title: 'Error', message: 'Store not found' });
      return;
    }

    if (!isAuthenticated && (!guestName.trim() || !guestEmail.trim())) {
      showErrorToast({ title: 'Error', message: 'Please fill in your name and email' });
      return;
    }

    setIsSubmitting(true);
    try {
      const config = apiConfig();
      const apiBase = import.meta.env.VITE_API_BASE_URL;
      const container = `shop-proof-${courier.id}`;

      // Step 1: If bank transfer with proof, upload file first
      let proofFilename: string | undefined;
      if (selectedPaymentMethod === 'bank_transfer' && uploadFile) {
        const formData = new FormData();
        formData.append('file', uploadFile);

        const uploadResponse = await fetch(
          `${apiBase}/Uploads/${container}/upload`,
          {
            method: 'POST',
            body: formData
          }
        );

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload proof of payment. Please try again.');
        }

        const uploadData = await uploadResponse.json();
        proofFilename = uploadData.result?.files?.file?.[0]?.name;

        if (!proofFilename) {
          throw new Error('Proof of payment upload failed. Please try again.');
        }
      }

      // Block bank transfer orders without proof
      if (selectedPaymentMethod === 'bank_transfer' && !proofFilename) {
        showErrorToast({ title: 'Error', message: 'Please upload proof of payment for bank transfer orders.' });
        return;
      }

      // Step 2: Create order with proof filename
      const orderData: Record<string, any> = {
        courierId: courier.id,
        status: 'pending',
        totalAmount: getTotalPrice(),
        paymentMethod: selectedPaymentMethod,
        paymentStatus: 'pending_verification',
        proofOfPayment: proofFilename,
        notes: orderNotes.trim() || undefined
      };

      if (isAuthenticated && user?.id) {
        orderData.customerId = user.id;
      } else {
        orderData.guestName = guestName.trim();
        orderData.guestEmail = guestEmail.trim();
        orderData.guestPhone = guestPhone.trim() || undefined;
        orderData.isGuestOrder = true;
      }

      const orderApi = new ShopOrderApi(apiConfig());
      const itemApi = new ShopOrderItemApi(apiConfig());

      const createdOrder = await orderApi.shopOrderCreate({
        data: orderData as any
      });

      // Step 3: Create order items
      for (const item of cartItems) {
        await itemApi.shopOrderItemCreate({
          data: {
            orderId: createdOrder.id,
            productId: item.productId,
            productName: item.name,
            price: item.price,
            quantity: item.quantity
          } as any
        });
      }

      // Step 4: If payment gateway, redirect to payment
      if (['stripe', 'wipay', 'fygaro'].includes(selectedPaymentMethod)) {
        clearCart();
        setOrderNotes('');
        window.location.href = `${apiBase}/ShopOrders/make-payment?orderId=${createdOrder.id}&method=${selectedPaymentMethod}`;
        return;
      }

      showSuccessToast({
        title: 'Success',
        message: selectedPaymentMethod === 'bank_transfer'
          ? 'Order placed! Your payment proof is being reviewed.'
          : 'Order placed successfully!'
      });
      clearCart();
      setOrderNotes('');
      setUploadFile(null);
      
      if (isAuthenticated) {
        navigate('/shop/orders');
      } else {
        navigate(`/shop/order-confirmation/${createdOrder.id}`);
      }
    } catch (error) {
      console.error('Error placing order:', error);
      showErrorToast({ title: 'Error', message: 'Failed to place order' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Order Summary sidebar (used in both desktop sidebar and mobile inline)
  const renderOrderSummary = () => (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 space-y-4">
      <h3 className="font-semibold text-gray-900 dark:text-white text-lg">Order Summary</h3>
      {cartItems.map((item) => (
        <div key={item.productId} className="flex justify-between text-sm">
          <span className="text-gray-600 dark:text-gray-400">
            {item.name} × {item.quantity}
          </span>
          <span className="font-medium text-gray-900 dark:text-white">
            JMD ${(item.price * item.quantity).toFixed(2)}
          </span>
        </div>
      ))}
      <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <span className="font-semibold text-gray-900 dark:text-white">Total:</span>
        <span className="font-bold text-xl text-gray-900 dark:text-white">
          JMD ${getTotalPrice().toFixed(2)}
        </span>
      </div>
    </div>
  );

  // Checkout View
  if (isCheckout) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900">
        <div className="max-w-6xl mx-auto p-4 lg:px-6 lg:py-8">
          {/* Desktop: two-column, Mobile: stacked */}
          <div className="lg:flex lg:gap-8">
            {/* Left: Forms */}
            <div className="lg:w-[60%] space-y-6">
              {/* Guest Details */}
              {!isAuthenticated && (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 space-y-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white text-lg">Your Details</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Full Name *</label>
                      <input
                        type="text"
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                        placeholder="John Doe"
                        required
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                          bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                          focus:ring-2 focus:ring-primary focus:border-transparent min-h-[44px]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email *</label>
                      <input
                        type="email"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                        placeholder="john@example.com"
                        required
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                          bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                          focus:ring-2 focus:ring-primary focus:border-transparent min-h-[44px]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={guestPhone}
                        onChange={(e) => setGuestPhone(e.target.value)}
                        placeholder="876-555-1234"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                          bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                          focus:ring-2 focus:ring-primary focus:border-transparent min-h-[44px]"
                      />
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Already have an account?{' '}
                    <Link to={`/auth?redirect=${encodeURIComponent('/shop/checkout')}`} className="text-primary hover:underline font-medium">
                      Sign in
                    </Link>
                  </p>
                </div>
              )}

              {/* Payment Method */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 space-y-4">
                <h3 className="font-semibold text-gray-900 dark:text-white text-lg">Payment Method</h3>
                
                <div className="space-y-3">
                  {courierSetting?.isStripeApiEnabled && (
                    <button 
                      onClick={() => setSelectedPaymentMethod('stripe')}
                      className={`w-full border rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-700 text-left min-h-[44px]
                        ${selectedPaymentMethod === 'stripe' ? 'border-primary bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-gray-600'}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900 dark:text-white">Stripe</span>
                        <CreditCard className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                      </div>
                    </button>
                  )}
                  
                  {courierSetting?.isFygaroApiEnabled && (
                    <button 
                      onClick={() => setSelectedPaymentMethod('fygaro')}
                      className={`w-full border rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-700 text-left min-h-[44px]
                        ${selectedPaymentMethod === 'fygaro' ? 'border-primary bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-gray-600'}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900 dark:text-white">Fygaro</span>
                        <CreditCard className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                      </div>
                    </button>
                  )}
                  
                  {courierSetting?.isWipayApiEnabled && (
                    <button 
                      onClick={() => setSelectedPaymentMethod('wipay')}
                      className={`w-full border rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-700 text-left min-h-[44px]
                        ${selectedPaymentMethod === 'wipay' ? 'border-primary bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-gray-600'}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900 dark:text-white">WiPay</span>
                        <CreditCard className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                      </div>
                    </button>
                  )}
                  
                  <div className={`border rounded-lg p-3 
                    ${selectedPaymentMethod === 'bank_transfer' ? 'border-primary bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-gray-600'}`}>
                    <label className="flex items-center justify-between cursor-pointer min-h-[44px]">
                      <span className="font-medium text-gray-900 dark:text-white">Bank Transfer</span>
                      <input
                        type="radio"
                        name="payment"
                        checked={selectedPaymentMethod === 'bank_transfer'}
                        onChange={() => setSelectedPaymentMethod('bank_transfer')}
                        className="h-4 w-4"
                      />
                    </label>
                    
                    {selectedPaymentMethod === 'bank_transfer' && (
                      <div className="mt-4 space-y-3">
                        {bankAccounts.length > 0 && (
                          <div className="space-y-2">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Transfer to one of these accounts:</span>
                            {bankAccounts.map((account) => (
                              <div key={account.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-sm space-y-1">
                                <div className="font-semibold text-gray-900 dark:text-white">{account.bankName}</div>
                                <div className="text-gray-700 dark:text-gray-300">
                                  <span className="text-gray-500 dark:text-gray-400">Name:</span> {account.accountName}
                                </div>
                                <div className="text-gray-700 dark:text-gray-300">
                                  <span className="text-gray-500 dark:text-gray-400">Account #:</span>{' '}
                                  <span className="font-mono">{account.accountNumber}</span>
                                </div>
                                {account.accountType && (
                                  <div className="text-gray-700 dark:text-gray-300">
                                    <span className="text-gray-500 dark:text-gray-400">Type:</span>{' '}
                                    {account.accountType.charAt(0).toUpperCase() + account.accountType.slice(1)}
                                    {account.accountCategory && ` (${account.accountCategory.charAt(0).toUpperCase() + account.accountCategory.slice(1)})`}
                                  </div>
                                )}
                                {account.branch && (
                                  <div className="text-gray-700 dark:text-gray-300">
                                    <span className="text-gray-500 dark:text-gray-400">Branch:</span> {account.branch}
                                  </div>
                                )}
                                {account.routingNumber && (
                                  <div className="text-gray-700 dark:text-gray-300">
                                    <span className="text-gray-500 dark:text-gray-400">Routing #:</span>{' '}
                                    <span className="font-mono">{account.routingNumber}</span>
                                  </div>
                                )}
                                {account.notes && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 italic mt-1">{account.notes}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        <label className="block">
                          <span className="text-sm text-gray-700 dark:text-gray-300">Upload Proof of Payment</span>
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                            className="mt-1 block w-full text-sm text-gray-500 dark:text-gray-400
                              file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0
                              file:text-sm file:font-medium file:bg-primary file:text-white
                              hover:file:bg-primary/90 file:min-h-[44px]"
                          />
                        </label>
                        {uploadFile && (
                          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                            <CheckCircle className="h-4 w-4 text-green-500 dark:text-green-400" />
                            <span>{uploadFile.name}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Order Notes (Optional)
                </label>
                <textarea
                  value={orderNotes}
                  onChange={(e) => setOrderNotes(e.target.value)}
                  placeholder="Any special instructions..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                    bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                    focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              {/* Mobile: Order summary + Place order (below forms) */}
              <div className="lg:hidden space-y-4">
                {renderOrderSummary()}

                <button
                  onClick={handlePlaceOrder}
                  disabled={isSubmitting || (selectedPaymentMethod === 'bank_transfer' && !uploadFile) || (!isAuthenticated && (!guestName.trim() || !guestEmail.trim()))}
                  className="w-full bg-green-500 hover:bg-green-600 text-white py-4 rounded-lg font-medium 
                    flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin text-white" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-5 w-5 mr-2" />
                      Place Order
                    </>
                  )}
                </button>

                {selectedPaymentMethod === 'bank_transfer' && !uploadFile && (
                  <div className="flex items-start gap-2 text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <p>Please upload proof of payment to complete your order.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Sticky sidebar (desktop only) */}
            <div className="hidden lg:block lg:w-[40%]">
              <div className="sticky top-6 space-y-4">
                {renderOrderSummary()}

                <button
                  onClick={handlePlaceOrder}
                  disabled={isSubmitting || (selectedPaymentMethod === 'bank_transfer' && !uploadFile) || (!isAuthenticated && (!guestName.trim() || !guestEmail.trim()))}
                  className="w-full bg-green-500 hover:bg-green-600 text-white py-4 rounded-xl font-medium 
                    flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] shadow-sm"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin text-white" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-5 w-5 mr-2" />
                      Place Order
                    </>
                  )}
                </button>

                {selectedPaymentMethod === 'bank_transfer' && !uploadFile && (
                  <div className="flex items-start gap-2 text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <p>Please upload proof of payment to complete your order.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Cart View (Default)
  return (
    <div className="bg-gray-50 dark:bg-gray-900">
      <div className="max-w-6xl mx-auto p-4 lg:px-6 lg:py-8">
        {/* Cart Items */}
        {cartItems.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingCart className="mx-auto h-16 w-16 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">Cart is empty</h3>
            <p className="mt-2 text-gray-500 dark:text-gray-400">
              Add some products to your cart
            </p>
            <Link 
              to="/shop"
              className="mt-6 inline-flex items-center justify-center px-6 py-3 bg-primary hover:bg-primary/90 
                text-white rounded-lg font-medium min-h-[44px]"
            >
              Continue Shopping
            </Link>
          </div>
        ) : (
          <div className="lg:flex lg:gap-8">
            {/* Left: Cart items */}
            <div className="lg:w-[60%] space-y-4">
              {/* Clear Cart Button */}
              <div className="flex items-center justify-between">
                <h2 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
                  Cart ({cartItems.length} {cartItems.length === 1 ? 'item' : 'items'})
                </h2>
                <button
                  onClick={clearCart}
                  className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 
                    text-sm font-medium min-h-[44px] px-3"
                >
                  Clear Cart
                </button>
              </div>

              <div className="space-y-3 lg:space-y-4">
                {cartItems.map((item) => (
                  <div key={item.productId} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex gap-4">
                      {item.image ? (
                        <div className="w-20 h-20 lg:w-24 lg:h-24 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                          <img
                            src={item.image.startsWith('http') ? item.image : `${import.meta.env.VITE_API_BASE_URL}/Uploads/${courier?.id}/download/${item.image}`}
                            alt={item.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"%3E%3Crect fill="%23ddd" width="80" height="80"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                            }}
                          />
                        </div>
                      ) : (
                        <div className="w-20 h-20 lg:w-24 lg:h-24 flex-shrink-0 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                          <ShoppingCart className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 dark:text-white truncate">{item.name}</h3>
                        {item.condition && (
                          <span className={`${getConditionBadge(item.condition)} mt-1`}>
                            {item.condition.replace('_', ' ')}
                          </span>
                        )}
                        <div className="mt-2 space-y-1">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            JMD ${item.price.toFixed(2)} each
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            Subtotal: JMD ${(item.price * item.quantity).toFixed(2)}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col items-end justify-between">
                        <button
                          onClick={() => removeFromCart(item.cartKey)}
                          className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 
                            p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>

                        <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                          <button
                            onClick={() => updateQuantity(item.cartKey, item.quantity - 1)}
                            className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-700 dark:text-gray-200"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="w-8 text-center font-medium text-gray-900 dark:text-white">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.cartKey, item.quantity + 1)}
                            className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-700 dark:text-gray-200"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Mobile: Notes + Subtotal below items */}
              <div className="lg:hidden space-y-4">
                {/* Order Notes */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Order Notes (Optional)
                  </label>
                  <textarea
                    value={orderNotes}
                    onChange={(e) => setOrderNotes(e.target.value)}
                    placeholder="Any special instructions..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                      bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                      focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                {/* Subtotal and Checkout */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 space-y-4">
                  <div className="flex justify-between text-lg font-semibold text-gray-900 dark:text-white">
                    <span>Subtotal:</span>
                    <span>JMD ${getTotalPrice().toFixed(2)}</span>
                  </div>
                  
                  <button
                    onClick={() => navigate('/shop/checkout')}
                    className="w-full bg-primary hover:bg-primary/90 text-white py-4 rounded-lg font-medium 
                      flex items-center justify-center min-h-[44px]"
                  >
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Proceed to Checkout
                  </button>
                </div>
              </div>
            </div>

            {/* Right: Sticky sidebar (desktop only) */}
            <div className="hidden lg:block lg:w-[40%]">
              <div className="sticky top-6 space-y-4">
                {/* Order Notes */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Order Notes (Optional)
                  </label>
                  <textarea
                    value={orderNotes}
                    onChange={(e) => setOrderNotes(e.target.value)}
                    placeholder="Any special instructions..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                      bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                      focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                {/* Subtotal and Checkout */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 space-y-4">
                  <div className="flex justify-between text-lg font-semibold text-gray-900 dark:text-white">
                    <span>Subtotal:</span>
                    <span>JMD ${getTotalPrice().toFixed(2)}</span>
                  </div>
                  
                  <button
                    onClick={() => navigate('/shop/checkout')}
                    className="w-full bg-primary hover:bg-primary/90 text-white py-4 rounded-xl font-medium 
                      flex items-center justify-center min-h-[44px] shadow-sm"
                  >
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Proceed to Checkout
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopCart;
